/* eslint-disable max-lines*/
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { AbstractControl, FormGroup } from '@angular/forms';
import { FormBuilder, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Subscription } from 'rxjs';
import { debounceTime, take } from 'rxjs';

import { invalidSelectedOptionValidator, noWhitespaceValidator } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { CountrySelectOptionsService, RegionsStoreService } from '@core/country-data';
import { I18nService } from '@core/i18n-legacy';
import { LanguagesService } from '@core/languages-legacy';
import type { LanguageItem } from '@core/languages-types';
import type { LocationLanguageTranslation } from '@core/locations-data';
import { LocationActions } from '@core/locations-data';
import type { CountryRegion, Location, LocationTranslation } from '@core/locations-types';
import type {
  DialogComponentI,
  LanguageSwitcherItem,
  SelectOption,
} from '@core/user-interface-legacy';
import { DialogRef, NotificationsController } from '@core/user-interface-legacy';
import { deepEqual } from '@core/utils-legacy';

import { manageLocationDialogImports } from './manage-location-dialog.imports';
import type { ManageLocationDialogData, LocationForm } from './manage-location-dialog.types';
import { LocationDialogContext } from './manage-location-dialog.types';
import { LocationsAnalytics } from '../../analytics/locations.analytics';
import { translatableLocationFields } from '../../types/locations-settings.types';

@Component({
  selector: 'rt-manage-location-dialog',
  imports: [manageLocationDialogImports, InfoboxComponent],
  templateUrl: './manage-location-dialog.component.html',
  styleUrls: ['./manage-location-dialog.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [LocationsAnalytics],
})
export class ManageLocationDialogComponent implements DialogComponentI<
  ManageLocationDialogData,
  boolean
> {
  private readonly dialog = inject(DialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly countrySelectOptionsService = inject(CountrySelectOptionsService);
  private readonly regionsStoreService = inject(RegionsStoreService);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly notifications = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly languageService = inject(LanguagesService);
  private readonly locationsAnalytics = inject(LocationsAnalytics);

  private readonly destroyRef = inject(DestroyRef);
  public readonly LocationDialogContext = LocationDialogContext;
  public readonly contextTranslations = {
    [LocationDialogContext.Create]: {
      heading: this.i18n.translate('app.locations.manage_location_dialog.create.heading'),
      subheading: this.i18n.translate('app.locations.manage_location_dialog.create.subheading'),
    },
    [LocationDialogContext.Edit]: {
      heading: this.i18n.translate('app.locations.manage_location_dialog.edit.heading'),
      subheading: this.i18n.translate('app.locations.manage_location_dialog.edit.subheading'),
    },
  };
  public readonly locationsUrl = '#/settings/locations';
  public dialogContext: LocationDialogContext;
  public form: FormGroup<LocationForm>;
  public countryOptions = this.countrySelectOptionsService.getAllCountrySelectOptions();
  public groupedCountryOptions = this.countrySelectOptionsService.getGroupedCountrySelectOptions();
  public regionsOptions: SelectOption[] = [];
  public fetchingRegions: boolean;
  public defaultLanguage: string;
  public saving = false;
  public isAssignedToActiveJobs: boolean;
  public fromJob?: boolean;
  public id: number | null;
  public activeLanguage: string;
  public languageSwitcherItems: null | LanguageSwitcherItem[] = null;
  public filledDefaultTranslatableFields: Array<
    keyof Pick<Location, 'name' | 'note' | 'city' | 'street' | 'postalCode'>
  > = [];
  public isMultilangEnabled = this.languageService.isMultilangEnabled;

  public goBackHandler?: () => void;

  private availableLanguages: LanguageItem[] = [];
  private saveHandler?: (createdLocationId: number) => void;
  private defaultTranslationSubscription: Subscription;
  private notDefaultTranslationsSubscription: Subscription[];

  public setDialogData(data: ManageLocationDialogData): void {
    this.dialogContext = data.context;
    this.activeLanguage = data.activeLanguage;
    this.availableLanguages = data.availableLanguages;
    this.languageSwitcherItems = data.availableLanguages.map(language => ({
      code: language.code,
      name: language.name,
      isDefault: language.isDefault,
      translationFieldsCount: 0,
      primary: language.primary,
    }));
    this.defaultLanguage = data.defaultLanguage;
    this.fromJob = data.fromJob;
    this.isAssignedToActiveJobs = !!data.location?.activeOffersCount;
    this.goBackHandler = data.goBackHandler;
    this.saveHandler = data.saveHandler;

    if (data.location) {
      this.id = data.location.id;
      this.createForm(data.location);
      this.setRegions(data.location.countryCode);
      data.location?.translations!.map(language =>
        this.setLangSwitcherCounter(language.langCode, language.filledCount),
      );
      const defaultTranslationValues = this.defaultTranslationValues;
      this.filledDefaultTranslatableFields = translatableLocationFields.filter(
        field => !!defaultTranslationValues[field],
      );
    } else {
      this.createForm();
    }

    this.subscribeToWebsocketActions(data);
  }

  public get isDefaultLanguage(): boolean {
    return this.activeLanguage === this.defaultLanguage;
  }

  public get defaultTranslationValues(): LocationLanguageTranslation {
    return <LocationLanguageTranslation>(
      this.form.controls.translations.value.find(
        translation => translation.langCode === this.defaultLanguage,
      )
    );
  }

  public isTranslationMarked(
    translationIndex: number,
    fieldName: keyof LocationLanguageTranslation,
  ): boolean {
    return (
      !this.isDefaultLanguage &&
      !!this.defaultTranslationValues[fieldName] &&
      !this.form.controls.translations.controls[translationIndex].value[fieldName]
    );
  }

  public close(): void {
    this.dialog.close();
  }

  public goBack() {
    this.goBackHandler && this.goBackHandler();
    this.close();
  }

  public save(): void {
    this.saving = true;
    this.cdRef.detectChanges();

    if (this.dialogContext === LocationDialogContext.Create) this.handleCreateAction();
    if (this.dialogContext === LocationDialogContext.Edit) this.handleEditAction();
  }

  public trackByControl(index: number, control: AbstractControl): AbstractControl {
    return control;
  }

  public setActiveLanguage(langCode: string): void {
    this.activeLanguage = langCode;
    this.cdRef.detectChanges();
    // triggers for updating view of potential country/region errors
    this.form.controls.countryCode.setErrors(this.form.controls.countryCode.errors);
    this.form.controls.stateCode.updateValueAndValidity();
  }

  private handleCreateAction(): void {
    this.store.dispatch(
      LocationActions.createOnePessimistic({
        payload: { data: this.form.getRawValue() },
      }),
    );
    this.actions$
      .pipe(ofType(LocationActions.createOnePessimisticSuccess), take(1))
      .subscribe(action => {
        this.notifications.success(
          this.i18n.translate('app.locations.manage_location_dialog.location_created'),
        );
        this.locationsAnalytics.trackCreateNew();
        this.saveHandler && this.saveHandler(action.payload.data.id);
        this.dialog.close(true);
      });
  }

  private handleEditAction(): void {
    this.store.dispatch(
      LocationActions.updateOnePessimistic({
        payload: { data: this.form.getRawValue(), id: this.id! },
      }),
    );
    this.actions$
      .pipe(ofType(LocationActions.updateOnePessimisticSuccess), take(1))
      .subscribe(action => {
        this.notifications.success(
          this.i18n.translate('app.locations.manage_location_dialog.location_edited'),
        );
        this.locationsAnalytics.trackEditLocation();
        this.saveHandler && this.saveHandler(action.payload.data.id);
        this.dialog.close(true);
      });
  }

  private createForm(location?: Location): void {
    const translations =
      location?.translations ||
      this.availableLanguages.map(lang => ({
        name: '',
        note: '',
        city: '',
        street: '',
        postalCode: '',
        langCode: lang.code,
      }));
    const allCountryOptions = this.countryOptions;

    this.form = this.fb.group({
      countryCode: this.fb.nonNullable.control(location?.countryCode || '', [
        Validators.required,
        invalidSelectedOptionValidator(allCountryOptions),
      ]),
      stateCode: this.fb.nonNullable.control(location?.stateCode || ''),
      translations: this.fb.nonNullable.array(
        translations.map(translation => this.createTranslationGroup(translation)),
      ),
    });

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.cdRef.detectChanges();
    });
    this.listenToCountryChange();
    this.listenToLangCounterChanges();
    this.verifyCountryValue();
  }

  private updateFormFromWS(location: Location): void {
    this.form.patchValue({
      countryCode: location.countryCode,
      stateCode: location.stateCode,
    });
    this.form.controls.translations.clear({ emitEvent: false });
    location.translations?.forEach(translation => {
      this.form.controls.translations.push(this.createTranslationGroup(translation));
    });
    this.form.controls.translations.updateValueAndValidity();
    this.listenToLangCounterChanges();
  }

  private setLangSwitcherCounter(landCode: string, count: number): void {
    this.languageSwitcherItems = this.languageSwitcherItems!.map(item => {
      if (item.code !== landCode) return item;
      return {
        ...item,
        translationFieldsCount: count,
      };
    });
  }

  private listenToLangCounterChanges(): void {
    if (this.defaultTranslationSubscription) {
      this.defaultTranslationSubscription.unsubscribe();
    }

    this.defaultTranslationSubscription = this.form.controls.translations.controls
      .find(translation => translation.controls.langCode.value === this.defaultLanguage)!
      .valueChanges.pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        const newFilledDefaultTranslatableFields = translatableLocationFields.filter(
          field => !!data[field],
        );
        const updateLangSwitcherCounter = !deepEqual(
          this.filledDefaultTranslatableFields,
          newFilledDefaultTranslatableFields,
        );
        this.filledDefaultTranslatableFields = newFilledDefaultTranslatableFields;

        if (updateLangSwitcherCounter) this.updateLangSwitcherCounters();

        this.cdRef.detectChanges();
      });

    if (this.notDefaultTranslationsSubscription?.length) {
      this.notDefaultTranslationsSubscription.forEach(
        subscription => subscription && subscription.unsubscribe(),
      );
    }

    this.notDefaultTranslationsSubscription = this.form.controls.translations.controls
      .filter(translation => translation.controls.langCode.value !== this.defaultLanguage)
      .map(translationFormGroup =>
        translationFormGroup.valueChanges
          .pipe(debounceTime(500), takeUntilDestroyed(this.destroyRef))
          .subscribe((data: LocationLanguageTranslation) => {
            this.setLangSwitcherCounter(
              data.langCode,
              this.filledDefaultTranslatableFields.filter(field => !!data[field]).length,
            );
            this.cdRef.detectChanges();
          }),
      );
  }

  private listenToCountryChange(): void {
    this.form.controls.countryCode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(countryCode => {
        if (!countryCode) {
          this.regionsOptions = [];
          return;
        }
        this.form.controls.stateCode.reset('');
        this.setRegions(countryCode);
      });
  }

  private setRegions(countryCode: string): void {
    this.fetchingRegions = true;
    this.cdRef.detectChanges();
    this.regionsStoreService
      .getOrLoadCountryRegions(countryCode)
      .pipe(take(1))
      .subscribe(regions => {
        this.form.controls.stateCode.clearValidators();
        this.regionsOptions = regions.map((region: CountryRegion) => ({
          label: region.name,
          value: region.code,
        }));

        if (regions.length) {
          this.form.controls.stateCode.addValidators(Validators.required);
          this.form.controls.stateCode.addValidators(
            invalidSelectedOptionValidator(this.regionsOptions),
          );
        }

        this.form.controls.stateCode.updateValueAndValidity();
        this.fetchingRegions = false;
        this.cdRef.detectChanges();
      });
  }

  private subscribeToWebsocketActions(data: ManageLocationDialogData) {
    if (data.updateLocation$) {
      data.updateLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(location => {
        if (this.form.pristine) {
          this.updateFormFromWS(location);
        }
      });
    }

    if (data.close$) {
      data.close$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.close();
      });
    }
  }

  private createTranslationGroup(
    translation: Pick<
      LocationTranslation,
      'name' | 'note' | 'city' | 'street' | 'postalCode' | 'langCode'
    >,
  ) {
    const { name, note, city, street, postalCode, langCode } = translation;
    const isDefault = translation.langCode === this.defaultLanguage;
    return this.fb.group({
      name: this.fb.nonNullable.control(name, [
        ...(isDefault ? [Validators.required] : []),
        Validators.maxLength(255),
      ]),
      note: this.fb.nonNullable.control(note, Validators.maxLength(500)),
      city: this.fb.nonNullable.control(city, [
        ...(isDefault
          ? [Validators.required, Validators.maxLength(255)]
          : [Validators.maxLength(255)]),
      ]),
      street: this.fb.nonNullable.control(street, [Validators.maxLength(255)]),
      postalCode: this.fb.nonNullable.control(postalCode, [
        noWhitespaceValidator,
        Validators.maxLength(255),
      ]),
      langCode: this.fb.nonNullable.control(langCode),
    });
  }

  private verifyCountryValue(): void {
    const allCountryOptions = this.countryOptions;
    const invalidCountryCode = !!invalidSelectedOptionValidator(allCountryOptions)(
      this.form.controls.countryCode,
    )?.invalidSelectedOption;
    if (invalidCountryCode) {
      this.form.controls.countryCode.updateValueAndValidity({ onlySelf: true, emitEvent: true });
    }
  }

  private updateLangSwitcherCounters(): void {
    this.languageSwitcherItems = this.languageSwitcherItems!.map(item => {
      if (item.primary) return item;
      const translationValue = this.form.controls.translations.controls.find(
        translation => translation.controls.langCode.value === item.code,
      )!.value;
      return {
        ...item,
        translationFieldsCount: this.filledDefaultTranslatableFields.filter(
          field => !!translationValue[field],
        ).length,
      };
    });
  }
}
